// utils/bluetoothPrinter.js
import WebBluetoothReceiptPrinter from '@point-of-sale/webbluetooth-receipt-printer';
import ReceiptPrinterEncoder from '@point-of-sale/receipt-printer-encoder';

class BluetoothReceiptPrinter {
    constructor() {
        this.printer = null;
        this.connectedDevice = null;
        this.encoder = null;
        this.isPrinting = false;
        this.listeners = {
            connected: [],
            disconnected: [],
            error: []
        };
    }

    /**
     * Add event listener
     */
    on(event, callback) {
        if (this.listeners[event]) {
            this.listeners[event].push(callback);
        }
    }

    /**
     * Remove event listener
     */
    off(event, callback) {
        if (this.listeners[event]) {
            this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
        }
    }

    /**
     * Emit event to all listeners
     */
    emit(event, data) {
        if (this.listeners[event]) {
            this.listeners[event].forEach(callback => callback(data));
        }
    }

    /**
     * Initialize printer (call after user interaction)
     */
    async initialize() {
        if (!this.isSupported()) {
            throw new Error('Web Bluetooth is not supported in this browser');
        }

        try {
            // Initialize the printer
            this.printer = new WebBluetoothReceiptPrinter();
            
            // Set up event listeners
            this.setupEventListeners();
            
            return true;
        } catch (error) {
            console.error('Failed to initialize printer:', error);
            throw error;
        }
    }

    /**
     * Set up event listeners
     */
    setupEventListeners() {
        if (!this.printer) return;

        this.printer.addEventListener('connected', (event) => {
            const device = event.detail || event;
            console.log(`✅ Connected to ${device.name || 'Printer'}`);
            this.connectedDevice = device;
            
            // Emit connected event
            this.emit('connected', {
                device: device,
                name: device.name,
                id: device.id
            });
            
            // Store device info for auto-reconnection
            localStorage.setItem('lastPrinter', JSON.stringify({
                id: device.id,
                name: device.name
            }));
        });

        this.printer.addEventListener('disconnected', () => {
            console.log('📴 Printer disconnected');
            this.connectedDevice = null;
            
            // Emit disconnected event
            this.emit('disconnected', null);
        });

        this.printer.addEventListener('connectionerror', (error) => {
            console.error('❌ Connection error:', error);
            this.connectedDevice = null;
            
            // Emit error event
            this.emit('error', error);
        });
    }

    /**
     * Connect to printer - MUST be called from user action (button click)
     */
    async connect() {
        if (!this.printer) {
            await this.initialize();
        }

        try {
            // Request Bluetooth device with specific filters for receipt printers
            const device = await navigator.bluetooth.requestDevice({
                filters: [
                    { namePrefix: 'G00JPRT' }, // Your specific printer
                    { namePrefix: 'POS' },
                    { namePrefix: 'Printer' },
                    { namePrefix: '58mm' },
                    { namePrefix: '80mm' },
                    { services: ['000018f0-0000-1000-8000-00805f9b34fb'] } // Common printer service
                ],
                optionalServices: [
                    '000018f0-0000-1000-8000-00805f9b34fb',
                    '00001800-0000-1000-8000-00805f9b34fb',
                    '00001801-0000-1000-8000-00805f9b34fb'
                ]
            });

            // Connect to the device
            await this.printer.connect(device);
            
            return device;
        } catch (error) {
            console.error('Failed to connect:', error);
            throw error;
        }
    }

    /**
     * Try to reconnect to last used printer
     */
    async reconnect() {
        const lastPrinter = localStorage.getItem('lastPrinter');
        if (!lastPrinter || !this.printer) {
            return false;
        }

        try {
            const deviceInfo = JSON.parse(lastPrinter);
            await this.printer.reconnect(deviceInfo);
            return true;
        } catch (error) {
            console.log('Auto-reconnect failed, will need manual connection');
            return false;
        }
    }

    /**
     * Print receipt
     */
/**
 * Print receipt
 */
async printReceipt(receiptData) {
    if (!this.printer) {
        await this.initialize();
    }

    if (!this.connectedDevice) {
        // Try to reconnect first
        const reconnected = await this.reconnect();
        if (!reconnected) {
            throw new Error('No printer connected. Please connect to printer first.');
        }
    }

    if (this.isPrinting) {
        throw new Error('Printer is busy printing another job');
    }

    this.isPrinting = true;

    try {
        // Create a NEW encoder instance each time
        const encoder = new ReceiptPrinterEncoder({
            language: 'esc-pos',
            width: 32, // 58mm paper typically 32 characters
            codepage: 'auto' // Auto-detect codepage
        });

        // Build the receipt
        const data = this.buildReceiptData(encoder, receiptData);
        
        // Print with proper formatting
        await this.printer.print(data);
        
        console.log('✅ Receipt printed successfully');
        
    } catch (error) {
        console.error('❌ Print failed:', error);
        throw error;
    } finally {
        this.isPrinting = false;
    }
}
    /**
     * Build receipt data with proper ESC/POS commands
     */
 /**
 * Build receipt data with proper ESC/POS commands
 */
buildReceiptData(encoder, receiptData) {
    const {
        storeName = 'YOUR STORE',
        storeAddress = '',
        storePhone = '',
        receiptNumber = this.generateReceiptNumber(),
        date = this.getCurrentDateTime(),
        cashier = 'Cashier',
        items = [],
        subtotal = 0,
        tax = 0,
        discount = 0,
        total = 0,
        payment = 0,
        change = 0,
        footer = 'Thank you for your purchase!'
    } = receiptData;

    // Create a new encoder instance instead of trying to clear
    const newEncoder = new ReceiptPrinterEncoder({
        language: 'esc-pos',
        width: 32,
        codepage: 'auto'
    });
    
    // Initialize printer with proper settings
    newEncoder
        .initialize()
        .codepage('auto')
        .lineSpacing(30);
    
    // Store header - centered
    newEncoder
        .align('center')
        .style('double')
        .text(storeName)
        .style('normal')
        .text(storeAddress)
        .text(storePhone)
        .newline()
        .text('================================')
        .newline();
    
    // Receipt info
    newEncoder
        .align('left')
        .text(`Receipt No: ${receiptNumber}`)
        .text(`Date: ${date}`)
        .text(`Cashier: ${cashier}`)
        .newline()
        .text('--------------------------------')
        .newline();
    
    // Items header
    newEncoder
        .style('bold')
        .text(this.formatItemLine('QTY', 'ITEM', 'AMOUNT'))
        .style('normal')
        .text('--------------------------------');
    
    // Items
    items.forEach(item => {
        const qty = item.quantity || item.qty || 1;
        const price = item.price || 0;
        const name = item.name || item.description || 'Item';
        const amount = price * qty;
        
        // Item name (truncate if too long)
        const truncatedName = this.truncateString(name, 20);
        newEncoder.text(truncatedName);
        
        // Quantity and price line
        const qtyStr = qty.toString().padStart(2);
        const priceStr = this.formatCurrency(price);
        const amountStr = this.formatCurrency(amount).padStart(8);
        newEncoder.text(`${qtyStr}  @${priceStr}  ${amountStr}`);
    });
    
    // Totals section
    newEncoder
        .newline()
        .text('--------------------------------')
        .align('right');
    
    if (subtotal > 0) {
        newEncoder.text(`Subtotal: ${this.formatCurrency(subtotal).padStart(8)}`);
    }
    
    if (discount > 0) {
        newEncoder.text(`Discount: -${this.formatCurrency(discount).padStart(8)}`);
    }
    
    if (tax > 0) {
        newEncoder.text(`Tax: ${this.formatCurrency(tax).padStart(8)}`);
    }
    
    newEncoder
        .style('bold')
        .text(`TOTAL: ${this.formatCurrency(total).padStart(8)}`)
        .style('normal')
        .text('--------------------------------');
    
    if (payment > 0) {
        newEncoder
            .align('left')
            .text(`Payment: ${this.formatCurrency(payment)}`)
            .text(`Change: ${this.formatCurrency(change)}`)
            .newline();
    }
    
    // Footer
    newEncoder
        .align('center')
        .text('================================')
        .text(footer)
        .text(new Date().toLocaleTimeString())
        .newline(3)
        .cut();
    
    return newEncoder.encode();
}

    /**
     * Format a line with columns
     */
    formatItemLine(col1, col2, col3) {
        return `${col1.padStart(3)} ${col2.padEnd(16)} ${col3.padStart(8)}`;
    }

    /**
     * Format total line
     */
    formatTotalLine(label, amount) {
        const line = `${label} ${amount}`;
        return line.padStart(32);
    }

    /**
     * Format currency
     */
    formatCurrency(amount) {
        return `₱${parseFloat(amount || 0).toFixed(2)}`;
    }

    /**
     * Truncate string to fit printer width
     */
    truncateString(str, maxLength) {
        if (!str) return '';
        if (str.length <= maxLength) return str;
        return str.substring(0, maxLength - 3) + '...';
    }

    /**
     * Generate receipt number
     */
    generateReceiptNumber() {
        const date = new Date();
        return `${date.getFullYear()}${(date.getMonth()+1).toString().padStart(2,'0')}${date.getDate().toString().padStart(2,'0')}-${date.getTime().toString().slice(-6)}`;
    }

    /**
     * Get current date and time formatted
     */
    getCurrentDateTime() {
        return new Date().toLocaleString('en-PH', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    }

    /**
     * Feed paper
     */
    async feedPaper(lines = 3) {
        if (!this.printer || !this.connectedDevice) return;
        
        try {
            const encoder = new ReceiptPrinterEncoder({ language: 'esc-pos' });
            const data = encoder.feed(lines).encode();
            await this.printer.print(data);
        } catch (error) {
            console.warn('Failed to feed paper:', error);
        }
    }

    /**
     * Cut paper
     */
    async cutPaper() {
        if (!this.printer || !this.connectedDevice) return;
        
        try {
            const encoder = new ReceiptPrinterEncoder({ language: 'esc-pos' });
            const data = encoder.cut().encode();
            await this.printer.print(data);
        } catch (error) {
            console.warn('Failed to cut paper:', error);
        }
    }

    /**
     * Disconnect printer
     */
    disconnect() {
        if (this.printer && this.connectedDevice) {
            this.printer.disconnect();
            this.connectedDevice = null;
            localStorage.removeItem('lastPrinter');
            
            // Emit disconnected event
            this.emit('disconnected', null);
        }
    }

    /**
     * Check if printer is connected
     */
    isConnected() {
        return this.connectedDevice !== null;
    }

    /**
     * Check if browser supports Web Bluetooth
     */
    isSupported() {
        return navigator.bluetooth !== undefined;
    }

    /**
     * Get connection status
     */
    getStatus() {
        return {
            connected: this.isConnected(),
            deviceName: this.connectedDevice?.name || null,
            supported: this.isSupported(),
            busy: this.isPrinting
        };
    }
}

// Export as singleton
const printer = new BluetoothReceiptPrinter();
export default printer;